import React, {useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {Button, CircularProgress, IconButton, InputAdornment} from "@mui/material";
import {Visibility, VisibilityOff} from "@mui/icons-material";
import {useSnackbar} from "../../../contexts/SnackbarContext";
import {Header, StyledCard, StyledForm, StyledTextField, Subtext} from "./ChangePassword.styled";
import {AuthContainer} from "../Auth.styled";
import {brand} from "../../../theme/brand";
import LogoWithLanguageSwitcher from '../../../components/common/LogoWithLanguageSwitcher';
import {usePageTitle} from '../../../hooks/usePageTitle';
import {PageTitleKeys} from '../../../config/routeConstants';
import {useTranslation} from 'react-i18next';
import {validatePassword, validatePasswordMatch} from "../../../utils/password-validation";
import {authService} from "../../../services/auth.service";


async function resetPassword(token: string, newPassword: string) {
    try {
        await authService.resetPassword(token, newPassword);
        return true;
    } catch (error) {
        console.error("Błąd zmiany hasła:", error);
        return false;
    }
}

const ChangePassword = () => {
    usePageTitle(PageTitleKeys.ChangePassword);
    const {t} = useTranslation();
    const {showSnackbar} = useSnackbar();
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordTouched, setPasswordTouched] = useState(false);
    const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [token, setToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const tokenParam = queryParams.get("token");

        if (!tokenParam) {
            navigate("/login");
            return;
        }

        setToken(tokenParam);
    }, [location, navigate]);

    const passwordValidationError = validatePassword(password);
    const confirmPasswordValidationError = validatePasswordMatch(password, confirmPassword);
    const showPasswordError = (passwordTouched || submitted) && !!passwordValidationError;
    const showConfirmPasswordError = (confirmPasswordTouched || submitted) && !!confirmPasswordValidationError;

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setPassword(value);
    };

    const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setConfirmPassword(value);
    };

    const handleSubmit = async () => {
        setSubmitted(true);
        if (passwordValidationError || confirmPasswordValidationError || !token) {
            return;
        }

        setIsLoading(true);
        try {
            const success = await resetPassword(token, password);
            if (success) {
                showSnackbar(t('changePassword.successMessage'), 'success');
                navigate("/login");
            } else {
                showSnackbar(t('changePassword.errorMessage'), 'error');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AuthContainer role="region" aria-labelledby="change-password-title">
            <LogoWithLanguageSwitcher src={brand.logo} alt={t('auth.logo.alt')} clickable={true} />
            <StyledCard>
                <Header id="change-password-title">{t('changePassword.title')}</Header>
                <Subtext>
                    {t('changePassword.subtitle')}
                </Subtext>

                <StyledForm autoComplete="off" aria-live="polite" aria-atomic={true} aria-busy={isLoading}>
                    <div>
                        <StyledTextField
                            label={t('changePassword.password')}
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={handlePasswordChange}
                            onBlur={() => setPasswordTouched(true)}
                            fullWidth
                            error={showPasswordError}
                            helperText={showPasswordError ? String(t(passwordValidationError!.key)) : ""}
                            disabled={isLoading}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            edge="end"
                                            sx={{color: 'primary.main'}}
                                            disabled={isLoading}
                                            aria-label={t('changePassword.password.toggleVisibility') as string}
                                        >
                                            {showPassword ? <VisibilityOff/> : <Visibility/>}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            inputProps={{ 'aria-label': t('changePassword.password.ariaLabel') as string }}
                        />
                    </div>

                    <div>
                        <StyledTextField
                            label={t('changePassword.confirmPassword')}
                            type={showConfirmPassword ? "text" : "password"}
                            value={confirmPassword}
                            onChange={handleConfirmPasswordChange}
                            onBlur={() => setConfirmPasswordTouched(true)}
                            fullWidth
                            error={showConfirmPasswordError}
                            helperText={showConfirmPasswordError ? String(t(confirmPasswordValidationError!.key)) : ""}
                            disabled={isLoading}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowConfirmPassword((prev) => !prev)}
                                            edge="end"
                                            sx={{color: 'primary.main'}}
                                            disabled={isLoading}
                                            aria-label={t('changePassword.confirmPassword.toggleVisibility') as string}
                                        >
                                            {showConfirmPassword ? <VisibilityOff/> : <Visibility/>}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            inputProps={{ 'aria-label': t('changePassword.confirmPassword.ariaLabel') as string }}
                        />
                    </div>

                    {isLoading && (
                        <div role="status" aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}>
                            {t('changePassword.loading')}
                        </div>
                    )}
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isLoading || !password || !confirmPassword || showPasswordError || showConfirmPasswordError}
                    >
                        {isLoading ? <CircularProgress size={24} sx={{color: 'inherit'}}/> : t('changePassword.saveButton')}
                    </Button>
                </StyledForm>
            </StyledCard>
        </AuthContainer>
    );
};

export default ChangePassword;
